import React, { useState, useEffect } from 'react';
import { DashboardFilters } from '@/components/DashboardFilters';
import { KPICard } from '@/components/KPICard';
import { ChartComponent } from '@/components/ChartComponent';
import { InsightCard } from '@/components/InsightCard';
import { RealtimeMetrics } from '@/components/RealtimeMetrics';
import { ExportDialog } from '@/components/ExportDialog';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { 
  mockKPIs, 
  mockInsights, 
  generateRevenueData, 
  generateEngagementData, 
  generateMarketingData,
  InsightData 
} from '@/data/mockData';
import { useToast } from '@/hooks/use-toast';

const Index = () => {
  const [revenueData, setRevenueData] = useState(generateRevenueData());
  const [engagementData, setEngagementData] = useState(generateEngagementData());
  const [marketingData, setMarketingData] = useState(generateMarketingData());
  const { toast } = useToast();

  const handlePeriodChange = (period: string) => {
    toast({
      title: "Período atualizado",
      description: `Dados filtrados para: ${period === '7d' ? 'Últimos 7 dias' : period === '30d' ? 'Últimos 30 dias' : period === '90d' ? 'Últimos 90 dias' : 'Último ano'}`,
    });
    
    // Simulate data refresh based on period
    setRevenueData(generateRevenueData());
    setEngagementData(generateEngagementData());
    setMarketingData(generateMarketingData());
  };

  const handleRefresh = async () => {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setRevenueData(generateRevenueData());
    setEngagementData(generateEngagementData());
    setMarketingData(generateMarketingData());
    
    toast({
      title: "Dados atualizados",
      description: "Dashboard atualizado com os dados mais recentes",
    });
  };



  const handleInsightAction = (insight: InsightData) => {
    toast({
      title: "Ação registrada",
      description: `Ação "${insight.action}" adicionada à sua lista de tarefas`,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header and Filters */}
        <DashboardFilters 
          onPeriodChange={handlePeriodChange}
          onRefresh={handleRefresh}
          exportDialogTrigger={
            <ExportDialog
              kpis={mockKPIs}
              revenueData={revenueData}
              engagementData={engagementData}
              marketingData={marketingData}
              insights={mockInsights}
            >
              <Button
                variant="outline"
                size="sm"
                className="transition-smooth"
              >
                <Download className="h-4 w-4 mr-2" />
                Exportar
              </Button>
            </ExportDialog>
          }
        />
        
        {/* Real-time Metrics */}
        <RealtimeMetrics />
        
        {/* KPI Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {mockKPIs.map((kpi) => (
            <KPICard key={kpi.id} data={kpi} />
          ))}
        </div>
        
        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartComponent
            title="Receita Mensal"
            description="Evolução da receita nos últimos 12 meses"
            data={revenueData}
            type="line"
            dataKey="revenue"
            color="hsl(217.2, 91.2%, 59.8%)"
          />
          
          <ChartComponent
            title="Usuários Ativos"
            description="Crescimento de usuários ativos mensais"
            data={revenueData}
            type="bar"
            dataKey="users"
            color="hsl(142.1, 76.2%, 36.3%)"
          />
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartComponent
            title="Engajamento Diário"
            description="Taxa de engajamento dos últimos 30 dias"
            data={engagementData}
            type="line"
            dataKey="value"
            color="hsl(32.2, 95%, 44.1%)"
          />
          
          <ChartComponent
            title="Canais de Marketing"
            description="Performance por canal de aquisição"
            data={marketingData}
            type="pie"
            dataKey="value"
          />
        </div>
        
        {/* Insights Section */}
        <div className="space-y-4">
          <h2 className="text-2xl font-bold text-gray-900">Insights Acionáveis</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {mockInsights.map((insight) => (
              <InsightCard 
                key={insight.id} 
                data={insight} 
                onActionClick={handleInsightAction}
              />
            ))}
          </div>
        </div>
        
        {/* Firebase Integration Guide */}
        <div className="mt-8 p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
          <h3 className="text-lg font-semibold mb-3 text-gray-900">🔥 Integração com Firebase</h3>
          <p className="text-gray-600 mb-4">
            Este dashboard está preparado para integração com Firebase. Para conectar seus dados reais:
          </p>
          <div className="space-y-2 text-sm text-gray-600">
            <p><strong>1.</strong> Configure o Firebase em seu projeto</p>
            <p><strong>2.</strong> Substitua os dados mock em <code className="bg-gray-100 px-2 py-1 rounded">src/data/mockData.ts</code></p>
            <p><strong>3.</strong> Implemente as funções de fetch do Firestore</p>
            <p><strong>4.</strong> Configure autenticação Firebase para múltiplas startups</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Index;
